'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useTheme } from '@/lib/ThemeContext';

const NAV_LINKS = [
  { label: 'PDF Tools', href: '/tools/pdf' },
  { label: 'Image Tools', href: '/tools/image' },
  { label: 'Audio Tools', href: '/tools/audio-tools' },
  { label: 'Scan', href: '/tools/scan' },
  { label: 'Text Tools', href: '/tools/text' },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstall, setShowInstall] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler as EventListener);
    return () => window.removeEventListener('beforeinstallprompt', handler as EventListener);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstall(false);
      setDeferredPrompt(null);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'shadow-lg'
            : ''
        }`}
        style={{
          background: scrolled
            ? theme === 'dark' ? 'rgba(15,23,42,0.92)' : 'rgba(255,255,255,0.82)'
            : theme === 'dark' ? 'rgba(15,23,42,0.80)' : 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderBottom: scrolled
            ? theme === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(37,99,235,0.10)'
            : theme === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(255,255,255,0.60)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 flex-shrink-0 group">
            <div
              className="w-9 h-9 rounded-2xl flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 60%, #7c3aed 100%)' }}
            >
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
              </svg>
            </div>
            <span
              className="font-display font-bold text-lg tracking-tight"
              style={{ color: theme === 'dark' ? '#f1f5f9' : '#0f172a' }}
            >
              mSuperTools
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {NAV_LINKS?.map((link) => (
              <Link
                key={link?.label}
                href={link?.href}
                onClick={() => setMenuOpen(false)}
                className="text-sm font-medium transition-colors px-3 py-2 rounded-xl"
                style={{
                  color: theme === 'dark' ? '#94a3b8' : '#64748b',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.color = theme === 'dark' ? '#93c5fd' : '#2563eb';
                  (e.currentTarget as HTMLElement).style.background = theme === 'dark' ? 'rgba(96,165,250,0.10)' : 'rgba(255,255,255,0.60)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.color = theme === 'dark' ? '#94a3b8' : '#64748b';
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Dark mode toggle */}
            <button
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 border"
              style={{
                background: theme === 'dark' ?'rgba(96,165,250,0.15)' :'rgba(255,255,255,0.70)',
                borderColor: theme === 'dark' ?'rgba(96,165,250,0.30)' :'rgba(255,255,255,0.60)',
                color: theme === 'dark' ? '#93c5fd' : '#64748b',
                boxShadow: theme === 'dark' ?'0 2px 8px rgba(96,165,250,0.15)' :'0 2px 8px rgba(15,23,42,0.06)',
              }}
            >
              {theme === 'dark' ? (
                <svg className="w-4.5 h-4.5" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                </svg>
              ) : (
                <svg className="w-4.5 h-4.5" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                </svg>
              )}
            </button>

            {/* PWA Install button */}
            {showInstall && (
              <button
                onClick={handleInstall}
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl transition-all duration-200 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 14px rgba(37,99,235,0.30)',
                }}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Install App
              </button>
            )}

            {/* Mobile hamburger */}
            <button
              className="md:hidden w-9 h-9 rounded-xl flex items-center justify-center transition-colors border"
              style={{
                background: theme === 'dark' ? 'rgba(30,41,59,0.80)' : 'rgba(255,255,255,0.60)',
                borderColor: theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.50)',
                color: theme === 'dark' ? '#94a3b8' : '#64748b',
              }}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div
            className="md:hidden border-t backdrop-blur-xl px-4 py-3 space-y-1"
            style={{
              background: theme === 'dark' ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.92)',
              borderColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.40)',
            }}
          >
            {NAV_LINKS?.map((link) => (
              <Link
                key={link?.label}
                href={link?.href}
                onClick={() => setMenuOpen(false)}
                className="block text-sm font-medium px-3 py-2.5 rounded-xl transition-colors"
                style={{ color: theme === 'dark' ? '#94a3b8' : '#64748b' }}
              >
                {link?.label}
              </Link>
            ))}
            {showInstall && (
              <button
                onClick={handleInstall}
                className="w-full mt-2 flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 14px rgba(37,99,235,0.30)',
                }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Install App
              </button>
            )}
          </div>
        )}
      </header>

      {/* Back to Top button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          aria-label="Back to top"
          className="fixed bottom-24 right-4 sm:bottom-8 sm:right-6 z-50 w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)',
            color: '#fff',
            boxShadow: '0 8px 24px rgba(37,99,235,0.35)',
          }}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
          </svg>
        </button>
      )}
    </>
  );
}